import React, { FC } from 'react';
import clsx from 'clsx';
import { kebabCase } from 'lodash-es';
import { Icon } from '@namespace/icons2';
import { AText } from '../AText';
import { Box } from '../Box';
import { SliderItems } from '../SliderItems';

import styles from './index.module.css';

interface SliderItemProps {
  label: string;
  id: string | number;
  icon?: string;
  selected: boolean;
  onChange: (id: string | number) => void;
  classNames?: {
    itemClassName?: string;
    activeClassName?: string;
    textClassName?: string;
    activeTextClassName?: string;
  };
  logo?: string;
  dataRole?: string;
}

const SliderItem: FC<SliderItemProps> = ({
  label,
  id,
  icon,
  selected,
  onChange,
  classNames = {},
  logo,
  dataRole
}) => {
  const {
    itemClassName = '',
    activeClassName = '',
    textClassName = '',
    activeTextClassName = ''
  } = classNames;

  return (
    <Box
      data-role={dataRole && `${dataRole}-tab-${kebabCase(label)}`}
      align="center"
      onClick={() => onChange(id)}
      className={clsx([
        styles.item,
        itemClassName,
        selected && `${styles.active} ${activeClassName}`
      ])}
    >
      {icon && <Icon name={icon} size="s" />}
      <AText
        className={clsx([
          styles.itemText,
          textClassName,
          selected && `${styles.textActive} ${activeTextClassName}`
        ])}
        size="m"
      >
        {label}
      </AText>
      {logo && (
        <Box
          component="img"
          alt={label}
          left="xxs"
          src={logo}
          width="14px"
          height="14px"
        />
      )}
    </Box>
  );
};

interface FiltersSliderProps {
  items: Array<{
    label: string;
    id: string | number;
    icon?: string;
    isActive?: boolean;
    logo?: string;
  }>;
  selectedItems?: Array<string | number>;
  onChange?: (id: string | number) => void;
  transformStep?: string;
  isButtonsEnabled?: boolean;
  isDesktop?: boolean;
  classNames?: {
    containerClassName?: string;
    buttonClassName?: string;
    [key: string]: unknown;
  };
  dataRole?: string;
}

export const FiltersSlider: FC<FiltersSliderProps> = ({
  items = [],
  selectedItems = [],
  onChange = () => {},
  transformStep,
  isButtonsEnabled = false,
  isDesktop,
  classNames = {},
  dataRole
}) => {
  const {
    containerClassName = '',
    buttonClassName = '',
    ...itemClassNames
  } = classNames;

  const sliderClassNames = { containerClassName, buttonClassName };

  return (
    <SliderItems
      dataRole={dataRole}
      isButtonsEnabled={isButtonsEnabled || false}
      isDesktop={isDesktop || false}
      transformStep={transformStep ? Number(transformStep) : undefined}
      classNames={sliderClassNames}
    >
      {items.map(({ label, id, icon, isActive, logo }) => (
        <SliderItem
          dataRole={dataRole}
          key={`filter-${id}`}
          selected={isActive || selectedItems.includes(id)}
          label={label}
          icon={icon}
          id={id}
          onChange={onChange}
          classNames={itemClassNames}
          logo={logo}
        />
      ))}
    </SliderItems>
  );
};
